import React, { useEffect, useState } from 'react';
import { User } from '../../types/auth';
import { usePredictionMarket, Market } from '../../hooks/usePredictionMarket';
import TxToast from '../common/TxToast';

interface ResolvePageProps {
  user: User | null;
}

export default function ResolvePage({ user }: ResolvePageProps) {
  const { markets, loadingMarkets, fetchMarkets, resolveMarket, tx, resetTx } =
    usePredictionMarket();

  const [resolveTarget, setResolveTarget] = useState<Market | null>(null);
  const [outcome, setOutcome] = useState<boolean>(true);
  const [confirmText, setConfirmText] = useState('');

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  if (!user?.isAdmin) {
    return (
      <div className="page-container">
        <div className="access-denied">
          <span className="access-denied-icon">🔒</span>
          <h2>Admin Access Required</h2>
          <p>Only administrators can resolve markets.</p>
        </div>
      </div>
    );
  }

  const unresolvedMarkets = markets.filter(m => !m.resolved);
  const resolvedMarkets = markets.filter(m => m.resolved);

  function openResolveModal(market: Market) {
    setResolveTarget(market);
    setOutcome(true);
    setConfirmText('');
    resetTx();
  }

  function closeModal() {
    setResolveTarget(null);
    resetTx();
  }

  async function handleResolve(e: React.FormEvent) {
    e.preventDefault();
    if (!resolveTarget) return;
    const success = await resolveMarket(resolveTarget.id, outcome);
    if (success) {
      setResolveTarget(null);
    }
  }

  function formatDeadline(ts: number) {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleString();
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2 className="page-title">⚖️ Resolve Markets</h2>
        <p className="page-subtitle">
          Settle expired markets by publishing the final outcome.
        </p>
      </div>

      <div className="resolve-toolbar">
        <button className="btn-outline" onClick={fetchMarkets} disabled={loadingMarkets}>
          {loadingMarkets ? '⟳ Loading…' : '⟳ Refresh'}
        </button>
        <span className="resolve-count">
          {unresolvedMarkets.length} market(s) awaiting resolution
        </span>
      </div>

      <TxToast tx={tx} onDismiss={resetTx} />

      {loadingMarkets ? (
        <div className="loading-state">Loading markets…</div>
      ) : unresolvedMarkets.length === 0 ? (
        <div className="empty-state">All markets have been resolved. ✓</div>
      ) : (
        <div className="resolve-list">
          {unresolvedMarkets.map(m => {
            const isExpired = m.deadline > 0 && Date.now() / 1000 > m.deadline;
            return (
              <div key={m.id} className={`resolve-card${isExpired ? ' resolve-card-expired' : ''}`}>
                <div className="resolve-card-meta">
                  <span className="market-id">#{m.id}</span>
                  {isExpired ? (
                    <span className="badge badge-expired">Expired – ready to resolve</span>
                  ) : (
                    <span className="badge badge-open">Active</span>
                  )}
                </div>
                <p className="market-question">{m.question}</p>
                <div className="market-meta">
                  <span>
                    YES pool: {parseFloat(m.yesPool).toFixed(4)} ETH
                  </span>
                  <span>
                    NO pool: {parseFloat(m.noPool).toFixed(4)} ETH
                  </span>
                  {m.deadline > 0 && (
                    <span>Deadline: {formatDeadline(m.deadline)}</span>
                  )}
                </div>
                <button
                  className="btn-primary"
                  onClick={() => openResolveModal(m)}
                >
                  ⚖️ Settle Market
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Resolved markets history */}
      {resolvedMarkets.length > 0 && (
        <div className="resolve-history">
          <h3 className="section-title">Resolved Markets</h3>
          <div className="resolve-list">
            {resolvedMarkets.map(m => (
              <div key={m.id} className="resolve-card resolve-card-done">
                <div className="resolve-card-meta">
                  <span className="market-id">#{m.id}</span>
                  <span className={`badge ${m.outcome ? 'badge-yes' : 'badge-no'}`}>
                    {m.outcome ? 'YES ✓' : 'NO ✗'}
                  </span>
                </div>
                <p className="market-question">{m.question}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Resolve Confirmation Modal */}
      {resolveTarget && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Settle Market #{resolveTarget.id}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close">
                ×
              </button>
            </div>

            <p className="modal-market-question">{resolveTarget.question}</p>

            <TxToast tx={tx} onDismiss={resetTx} />

            <form onSubmit={handleResolve} noValidate>
              <div className="form-group">
                <label className="form-label">Outcome</label>
                <div className="prediction-toggle">
                  <button
                    type="button"
                    className={`toggle-btn toggle-yes${outcome ? ' active' : ''}`}
                    onClick={() => setOutcome(true)}
                  >
                    YES – Condition Met
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn toggle-no${!outcome ? ' active' : ''}`}
                    onClick={() => setOutcome(false)}
                  >
                    NO – Condition Not Met
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="resolve-confirm">
                  Type <strong>CONFIRM</strong> to proceed
                </label>
                <input
                  id="resolve-confirm"
                  className="form-input"
                  type="text"
                  placeholder="CONFIRM"
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                />
              </div>

              <button
                type="submit"
                className="btn-primary btn-full btn-danger"
                disabled={tx.status === 'pending' || confirmText !== 'CONFIRM'}
              >
                {tx.status === 'pending'
                  ? '⏳ Submitting…'
                  : `Resolve as ${outcome ? 'YES' : 'NO'}`}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
